import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { ApiError } from '../../lib/api';
import { operatorConsole, type MediaKitPack } from '../../lib/api/operatorConsole';
import { LoadError } from './LoadError';
import { PackTile } from './PackTile';
import { errorText } from './mediaKitErrors';

export function MediaKitPage() {
  const [packs, setPacks] = useState<MediaKitPack[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    operatorConsole.mediaKit
      .listPacks()
      .then((loaded) => {
        if (alive) setPacks(loaded);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        // 404 = the operator has no Media Kit (the backend hides the feature
        // entirely) — leave without a trace, like any unknown URL.
        if (err instanceof ApiError && err.status === 404) {
          setHidden(true);
          return;
        }
        setError(errorText(err));
      });
    return () => {
      alive = false;
    };
  }, [attempt]);

  function retry() {
    setError(null);
    setAttempt((n) => n + 1);
  }

  if (hidden) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold">Media Kit</h1>
        <p className="text-[12px] text-[var(--co-muted)]">
          Original, full-resolution artwork for your marketing — pack art and top cards of every pack
          enabled for you.
        </p>
      </div>

      {error ? (
        <LoadError message={error} onRetry={retry} />
      ) : packs === null ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading packs…</p>
      ) : packs.length === 0 ? (
        <div className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 text-center text-[13px] text-[var(--co-muted)]">
          No packs are enabled for you yet.
        </div>
      ) : (
        <ul
          aria-label="Packs"
          className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
          {packs.map((pack) => (
            <li key={pack.id} className="flex">
              <PackTile pack={pack} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
